import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Checkbox,
  Avatar,
  Button,
  Grid,
  Collapse,
  Chip
} from '@mui/material';
import NumericTextField from './NumericTextField';
import { isIncludedPackageItem } from '../utils/packageItems';
import {
  tripDateTimeBounds,
  localDateTimeValue,
  clampToTrip,
  defaultTimesInTrip,
  isWithinTrip,
} from '../utils/tripScheduleBounds';
import {
  Person as PersonIcon,
  Group as GroupIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocalActivity as ActivityIcon,
  Restaurant as RestaurantIcon,
  Hotel as HotelIcon,
  DirectionsCar as TransportIcon,
  Celebration as EventIcon,
  MoreHoriz as OtherIcon,
} from '@mui/icons-material';
import LocationAutocomplete from './LocationAutocomplete';
import FlightLegsEditor from './FlightLegsEditor';
import { TRANSPORTATION_TYPES, TRANSPORTATION_CATEGORIES, getTransportationType } from '../utils/transportationTypes';
import {
  buildTransportationDataFromLegs,
  legsToInitialFormLegs,
  getJourneyRouteLabel,
} from '../utils/flightJourneyUtils';
import {
  deriveScheduleDatetimes,
  getActivityScheduleMode,
  seedTransportTimesFromSchedule,
} from '../utils/activityScheduleMode';
import { CURRENCIES } from '../constants/currencies';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { DOCUMENT, documentFormLabelSx, documentOutlineButtonSx, documentSubSectionTitleSx } from '../constants/documentTheme';
import { SingleImageUploader } from './ImageUploader';
import {
  PARTICIPANT_STATUS_LABEL_KEYS,
  getParticipantKey,
  normalizeParticipantKey,
  toParticipantKeySet,
} from '../utils/memberItineraryParticipants';

const CATEGORY_OPTIONS = [
  { value: 'activity', icon: ActivityIcon, labelKey: 'activityModal.activity' },
  { value: 'restaurant', icon: RestaurantIcon, labelKey: 'activityModal.restaurant' },
  { value: 'hotel', icon: HotelIcon, labelKey: 'activityModal.hotel' },
  { value: 'transport', icon: TransportIcon, labelKey: 'activityModal.transport' },
  { value: 'event', icon: EventIcon, labelKey: 'activityModal.event' },
  { value: 'other', icon: OtherIcon, labelKey: 'activityModal.other' },
];

const FLIGHT_BOOKING_FIELDS = [
  { name: 'confirmation_number', labelKey: 'confirmationNumber' },
  { name: 'seat', labelKey: 'seat' },
  { name: 'gate', labelKey: 'gate' },
  { name: 'terminal', labelKey: 'terminal' },
  { name: 'baggage', labelKey: 'baggage' },
  { name: 'status', labelKey: 'status' },
];

const CategoryOption = ({ option, label }) => {
  const Icon = option.icon;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Icon sx={{ fontSize: 18, color: DOCUMENT.ink2 }} />
      <Box component="span">{label}</Box>
    </Box>
  );
};

const underlineSx = {
  '& .MuiInput-underline:before': { borderBottomColor: DOCUMENT.ruleHard },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: DOCUMENT.accent },
  '& .MuiInput-underline:after': { borderBottomColor: DOCUMENT.accent },
  '& .MuiInputBase-root': { pl: 0, pr: 0, width: '100%' },
  '& .MuiInputBase-input': {
    fontSize: 16,
    py: 1.25,
    pl: 0,
    pr: 0,
    color: DOCUMENT.ink,
    lineHeight: 1.5,
    overflow: 'visible',
  },
  '& .MuiInputBase-input::placeholder': { color: DOCUMENT.placeholder, opacity: 1 },
  '& .MuiSelect-select': { fontSize: 16, py: 1.25, color: DOCUMENT.ink },
  '& .MuiFormHelperText-root': { color: DOCUMENT.ink3 },
};

const locationSx = underlineSx;

const ghostSx = {
  background: 'none',
  border: 0,
  font: 'inherit',
  fontSize: 11,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink2,
  cursor: 'pointer',
  p: 0,
  pb: '2px',
  borderBottom: '1px solid transparent',
  '&:hover': { color: DOCUMENT.ink, borderBottomColor: DOCUMENT.accent },
};

const primaryBtnSx = {
  bgcolor: DOCUMENT.oxblood,
  color: DOCUMENT.ivory,
  borderRadius: 0,
  px: 2.75,
  py: 1.5,
  fontSize: 11,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  boxShadow: 'none',
  '&:hover': { bgcolor: DOCUMENT.nav, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: DOCUMENT.ruleHard, color: '#fff' },
};

const Field = ({ label, required, hint, children }) => (
  <Box sx={{ py: 2.1, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
    <Typography sx={documentFormLabelSx}>
      {label}
      {required && <Box component="span" sx={{ color: DOCUMENT.accent, ml: 0.4 }}>*</Box>}
    </Typography>
    {children}
    {hint ? <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 1, lineHeight: 1.55 }}>{hint}</Typography> : null}
  </Box>
);

const Section = ({ title, children }) => (
  <Box sx={{ mb: 1 }}>
    <Typography
      component="h3"
      sx={{
        ...documentSubSectionTitleSx,
        pb: 1.35,
        borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
        mb: 0.5,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const ActivityModal = ({ 
  open, 
  onClose, 
  onSubmit, 
  mode = 'create', 
  initialData = {},
  tripMembers = [],
  personalOwnerOptions = [],
  canAssignPersonalOwner = false,
  submitting = false,
  submitError = null,
  onClearSubmitError = null,
  showVisibility = true,
  // When false, visibility comes from the Personal / Sub-Groups tab — only
  // show the owner or participant pickers that apply to that mode.
  showVisibilitySelect = true,
  showCost = true,
  showPackageAddOn = false,
  defaultVisibility = 'group',
  trip = null,
  usePartnerAuth = false,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    google_map_link: '',
    latitude: null,
    longitude: null,
    place_id: null,
    geo_source: null,
    category: 'activity',
    start_datetime: '',
    end_datetime: '',
    description: '',
    image_url: '',
    cost: '',
    cost_currency: 'USD',
    visibility: defaultVisibility,
    participants: [],
    owner_user_id: null,
    transportation_type: '',
    transportation_data: {},
    is_included: true
  });

  const [errors, setErrors] = useState({});
  const [localSubmitError, setLocalSubmitError] = useState(null);
  const displayedSubmitError = submitError || localSubmitError;
  const selectedParticipantKeys = toParticipantKeySet(formData.participants || []);
  const [showTransportationFields, setShowTransportationFields] = useState(false);
  const [flightLegs, setFlightLegs] = useState([{
    airline: '',
    flight_number: '',
    departure_airport: '',
    arrival_airport: '',
    departure_time: '',
    arrival_time: '',
  }]);

  useEffect(() => {
    if (open && initialData) {
      setLocalSubmitError(null);
      setErrors({});

      const bounds = tripDateTimeBounds(trip);
      let startDateTime = initialData.start_datetime || '';
      let endDateTime = initialData.end_datetime || '';

      if (!startDateTime && !endDateTime) {
        const defaults = defaultTimesInTrip(bounds);
        startDateTime = defaults.start;
        endDateTime = defaults.end;
      } else {
        startDateTime = clampToTrip(startDateTime, bounds);
        endDateTime = clampToTrip(endDateTime, bounds);
        if (startDateTime && endDateTime && endDateTime < startDateTime) {
          endDateTime = startDateTime;
        }
      }
      
      const isIncluded = isIncludedPackageItem(initialData);
      const initialTransportType = initialData.transportation_type || '';
      const initialScheduleMode = getActivityScheduleMode({
        category: initialData.category || 'activity',
        transportationType: initialTransportType,
      });
      const { transportationData: seededTransportData } = seedTransportTimesFromSchedule({
        transportationData: initialData.transportation_data || {},
        startDatetime: startDateTime,
        endDatetime: endDateTime,
        requiredFields: initialScheduleMode.requiredTransportTimeFields || [],
      });
      setFormData({
        name: initialData.name || '',
        location: initialData.location || '',
        google_map_link: initialData.google_map_link || '',
        latitude: initialData.latitude != null ? Number(initialData.latitude) : null,
        longitude: initialData.longitude != null ? Number(initialData.longitude) : null,
        place_id: initialData.place_id || null,
        geo_source: initialData.geo_source || null,
        category: initialData.category || 'activity',
        start_datetime: startDateTime,
        end_datetime: endDateTime,
        description: initialData.description || '',
        image_url: initialData.image_url || '',
        cost: showPackageAddOn && !isIncluded
          ? (initialData.additional_cost || initialData.cost || '')
          : (initialData.cost || ''),
        cost_currency: initialData.cost_currency || initialData.currency || 'USD',
        visibility: initialData.visibility || defaultVisibility,
        participants: initialData.participants || [],
        owner_user_id: initialData.owner_user_id != null ? Number(initialData.owner_user_id) : null,
        transportation_type: initialTransportType,
        transportation_data: seededTransportData,
        is_included: isIncluded
      });

      const isFlightTransport =
        initialTransportType === 'flight' ||
        initialData.category === 'flight';
      if (isFlightTransport) {
        setFlightLegs(legsToInitialFormLegs(initialData));
        setShowTransportationFields(true);
      } else {
        setFlightLegs([{
          airline: '',
          flight_number: '',
          departure_airport: '',
          arrival_airport: '',
          departure_time: '',
          arrival_time: '',
        }]);
        // Car rental / train / etc. hide Schedule and require times inside this panel.
        setShowTransportationFields(
          Boolean(initialTransportType)
          && (
            initialScheduleMode.source === 'transport_fields'
            || initialScheduleMode.source === 'transport_start_only'
          )
        );
      }
      
      console.log('Final form data set:', {
        transportation_type: initialData.transportation_type || '',
        transportation_data: initialData.transportation_data || {}
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const scheduleMode = getActivityScheduleMode({
    category: formData.category,
    transportationType: formData.transportation_type,
  });

  // Keep Transportation Details open whenever Schedule is replaced by transport times.
  useEffect(() => {
    if (
      scheduleMode.source === 'transport_fields'
      || scheduleMode.source === 'transport_start_only'
      || scheduleMode.source === 'flight_legs'
    ) {
      setShowTransportationFields(true);
    }
  }, [scheduleMode.source, formData.transportation_type]);

  // Keep canonical schedule in sync with flight legs while editing.
  useEffect(() => {
    if (scheduleMode.source !== 'flight_legs') {
      return;
    }
    const transportationData = buildTransportationDataFromLegs(
      flightLegs,
      formData.transportation_data || {}
    );
    const nextStart = transportationData.departure_time || '';
    const nextEnd = transportationData.arrival_time || '';
    if (
      nextStart === formData.start_datetime
      && nextEnd === formData.end_datetime
    ) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      start_datetime: nextStart || prev.start_datetime,
      end_datetime: nextEnd || prev.end_datetime,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightLegs, scheduleMode.source]);

  const clearSubmitErrors = () => {
    if (localSubmitError) setLocalSubmitError(null);
    if (submitError && typeof onClearSubmitError === 'function') {
      onClearSubmitError();
    }
  };

  const validateForm = (data = formData) => {
    const newErrors = {};
    const mode = getActivityScheduleMode({
      category: data.category,
      transportationType: data.transportation_type,
    });
    
    if (!String(data.name || '').trim()) {
      newErrors.name = t('itinerary.activityNameRequired', {
        defaultValue: 'Activity name is required',
      });
    }

    // Backend create requires location; flights can derive it from the route.
    if (mode.source !== 'flight_legs' && !String(data.location || '').trim()) {
      newErrors.location = t('itinerary.locationRequired', {
        defaultValue: 'Location is required',
      });
    }

    if (mode.source === 'flight_legs') {
      const transportationData = buildTransportationDataFromLegs(
        flightLegs,
        data.transportation_data || {}
      );
      if (!transportationData.departure_time || !transportationData.arrival_time) {
        newErrors.schedule = t('itinerary.flightTimesRequired');
      }
    } else if (mode.source === 'transport_fields') {
      (mode.requiredTransportTimeFields || []).forEach((fieldName) => {
        if (!data.transportation_data?.[fieldName]) {
          newErrors[fieldName] = t('itinerary.transportTimeRequired');
        }
      });
    } else if (mode.source === 'transport_start_only') {
      (mode.requiredTransportTimeFields || []).forEach((fieldName) => {
        if (!data.transportation_data?.[fieldName]) {
          newErrors[fieldName] = t('itinerary.transportTimeRequired');
        }
      });
      if (!data.end_datetime) {
        newErrors.end_datetime = t('itinerary.endDateTimeRequired', {
          defaultValue: 'End date and time is required',
        });
      }
    } else {
      if (!data.start_datetime) {
        newErrors.start_datetime = t('itinerary.startDateTimeRequired', {
          defaultValue: 'Start date and time is required',
        });
      }
      if (!data.end_datetime) {
        newErrors.end_datetime = t('itinerary.endDateTimeRequired', {
          defaultValue: 'End date and time is required',
        });
      }
      const bounds = tripDateTimeBounds(trip);
      const outside = t('itinerary.dateOutsideTrip', {
        defaultValue: 'Choose a time within the trip dates',
      });
      if (!isWithinTrip(data.start_datetime, bounds)) {
        newErrors.start_datetime = outside;
      }
      if (!isWithinTrip(data.end_datetime, bounds)) {
        newErrors.end_datetime = outside;
      }
    }
    
    if (data.visibility === 'subgroup' && (data.participants || []).length === 0) {
      newErrors.participants = 'Please select at least one participant for subgroup activities';
    }

    if (
      data.visibility === 'personal'
      && canAssignPersonalOwner
      && personalOwnerOptions.length > 0
      && !data.owner_user_id
    ) {
      newErrors.owner_user_id = t('activityModal.personalOwnerRequired', {
        defaultValue: 'Choose whose personal itinerary this belongs to.',
      });
    }

    if (showPackageAddOn && data.is_included === false) {
      const value = Number(data.cost);
      if (!Number.isFinite(value) || value <= 0) {
        newErrors.cost = t('activityModal.addOnPriceRequired');
      }
    }
    
    setErrors(newErrors);
    const transportTimeError = (mode.requiredTransportTimeFields || []).some(
      (fieldName) => newErrors[fieldName]
    );
    if (transportTimeError || newErrors.schedule) {
      setShowTransportationFields(true);
      if (!newErrors.schedule && transportTimeError) {
        newErrors.schedule = t('itinerary.transportTimesRequiredVisible', {
          defaultValue: 'Open Transportation Details and set pickup/return (or departure/arrival) times.',
        });
        setErrors({ ...newErrors });
      }
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearSubmitErrors();

    // Inherit day schedule into required transport times before validating so
    // Create isn't blocked by a collapsed Transportation Details section.
    let submitData = { ...formData };
    if ((scheduleMode.requiredTransportTimeFields || []).length) {
      const { transportationData, changed } = seedTransportTimesFromSchedule({
        transportationData: formData.transportation_data || {},
        startDatetime: formData.start_datetime,
        endDatetime: formData.end_datetime,
        requiredFields: scheduleMode.requiredTransportTimeFields,
      });
      if (changed) {
        submitData = { ...submitData, transportation_data: transportationData };
        setFormData(submitData);
      }
    }

    if (!validateForm(submitData)) return;

    if (formData.transportation_type === 'flight') {
      const transportationData = buildTransportationDataFromLegs(flightLegs, formData.transportation_data || {});
      const journeyPreview = {
        transportation_data: transportationData,
        category: 'transport',
        transportation_type: 'flight',
      };
      const routeLabel = getJourneyRouteLabel({
        legs: transportationData.legs || [],
        items: [journeyPreview],
      });

      submitData = {
        ...submitData,
        category: submitData.category === 'activity' ? 'transport' : submitData.category,
        transportation_data: transportationData,
        name: submitData.name.trim() || routeLabel || t('itinerary.flight', { count: 1 }),
        location: String(submitData.location || '').trim() || routeLabel || submitData.location,
      };
    }

    // Always populate canonical schedule from flight/transport times when
    // the Schedule fields are hidden or start comes from pickup only.
    const derivedSchedule = deriveScheduleDatetimes({
      scheduleMode: getActivityScheduleMode({
        category: submitData.category,
        transportationType: submitData.transportation_type,
      }),
      transportationData: submitData.transportation_data,
      startDatetime: submitData.start_datetime,
      endDatetime: submitData.end_datetime,
    });
    submitData = {
      ...submitData,
      start_datetime: derivedSchedule.start_datetime,
      end_datetime: derivedSchedule.end_datetime,
    };

    if (showPackageAddOn) {
      const isIncluded = formData.is_included !== false;
      submitData = {
        ...submitData,
        is_included: isIncluded,
        additional_cost: isIncluded ? 0 : Number(formData.cost || 0),
      };
    }

    try {
      await onSubmit(submitData);
    } catch (err) {
      // Parents usually catch and pass submitError; this is a fallback so the
      // button never looks "dead" with no message inside the fullscreen modal.
      const message = err?.response?.data?.error || err?.message
        || t('itinerary.saveFailed', { defaultValue: 'Could not save. Please try again.' });
      setLocalSubmitError(message);
    }
  };

  const handleFieldChange = (field, value) => {
    clearSubmitErrors();
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-set end time when start time is set (only for new activities with no end time)
      const bounds = tripDateTimeBounds(trip);
      if (field === 'start_datetime' || field === 'end_datetime') {
        newData[field] = clampToTrip(value, bounds);
      }
      if (field === 'start_datetime' && newData.start_datetime && !prev.end_datetime && !initialData?.end_datetime) {
        const startDate = new Date(newData.start_datetime);
        const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
        newData.end_datetime = clampToTrip(localDateTimeValue(endDate), bounds);
      }
      if (newData.start_datetime && newData.end_datetime && newData.end_datetime < newData.start_datetime) {
        newData.end_datetime = newData.start_datetime;
      }
      
      return newData;
    });
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };


  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={true}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 0,
          boxShadow: 'none',
          bgcolor: DOCUMENT.canvas,
          color: DOCUMENT.ink,
          overflow: 'hidden',
          maxHeight: { xs: '100vh', sm: '80vh' },
          width: { xs: '100vw', sm: '640px' },
          maxWidth: { xs: '100vw', sm: '640px' },
          height: { xs: '100vh', sm: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          position: { xs: 'fixed', sm: 'relative' },
          top: { xs: 0, sm: 'auto' },
          left: { xs: 0, sm: 'auto' },
          right: { xs: 0, sm: 'auto' },
          bottom: { xs: 0, sm: 'auto' },
          margin: 0,
          border: { sm: `1px solid ${DOCUMENT.ruleHard}` },
        },
        '& .MuiDialog-container': {
          overflow: 'hidden',
          position: { xs: 'fixed', sm: 'relative' },
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'center',
          bgcolor: { sm: 'rgba(36,19,24,0.28)' },
        }
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          bgcolor: DOCUMENT.canvas,
          color: DOCUMENT.ink,
          py: 2.5,
          px: { xs: 2.25, sm: 3.5 },
          borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
        }}
      >
        <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, fontSize: { xs: 26, sm: 30 }, lineHeight: 1.1 }}>
          {mode === 'create' ? t('itinerary.addNewActivity') : t('itinerary.editActivity')}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 1, lineHeight: 1.55 }}>
          {mode === 'create' ? t('itinerary.createNewActivityDesc') : t('itinerary.updateActivityDesc')}
        </Typography>
      </DialogTitle>
      
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <DialogContent sx={{ 
          p: { xs: 2.25, sm: 3.5 },
          pt: { xs: 2, sm: 2.5 },
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          WebkitOverflowScrolling: 'touch',
          bgcolor: DOCUMENT.canvas,
          maxHeight: 'calc(100vh - 160px)',
        }}>
          {(displayedSubmitError || errors.schedule) ? (
            <Alert
              severity="error"
              onClose={() => {
                clearSubmitErrors();
                if (errors.schedule) {
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next.schedule;
                    return next;
                  });
                }
              }}
              sx={{
                mb: 2,
                borderRadius: 0,
                border: `1px solid ${DOCUMENT.ruleHard}`,
                bgcolor: DOCUMENT.ivory,
                color: DOCUMENT.ink,
                '& .MuiAlert-icon': { color: DOCUMENT.oxblood },
              }}
            >
              {displayedSubmitError || errors.schedule}
            </Alert>
          ) : null}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            <Section title={t('itinerary.basicInformation')}>
                <Field label={t('itinerary.activityNameLabel')} required>
                  <TextField
                    name="name"
                    placeholder={t('itinerary.activityNamePlaceholder')}
                    value={formData.name}
                    onChange={(e) => {
                      console.log('Name field onChange called with:', e.target.value);
                      handleFieldChange('name', e.target.value);
                    }}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                    fullWidth
                    variant="standard"
                    InputLabelProps={{ shrink: false }}
                    inputProps={{ 'aria-label': t('itinerary.activityNameLabel') }}
                    sx={underlineSx}
                  />
                </Field>
                
                <Field label={t('itinerary.locationLabel')} required>
                  <LocationAutocomplete
                    name="location"
                    label=""
                    variant="standard"
                    placeholder={t('itinerary.searchLocationPlaceholder')}
                    value={formData.location}
                    tripId={trip?.id || null}
                    usePartnerAuth={Boolean(usePartnerAuth)}
                    onChange={(newValue) => {
                      clearSubmitErrors();
                      setFormData((prev) => ({
                        ...prev,
                        location: newValue,
                        // Typing by hand clears stored coords so view mode re-geocodes.
                        latitude: null,
                        longitude: null,
                        place_id: null,
                        geo_source: null,
                      }));
                      if (errors.location) {
                        setErrors((prev) => ({ ...prev, location: '' }));
                      }
                    }}
                    onPlaceSelect={(placeData) => {
                      clearSubmitErrors();
                      setFormData((prev) => ({
                        ...prev,
                        location: placeData.address,
                        google_map_link: placeData.googleMapsLink || prev.google_map_link,
                        latitude: placeData.coordinates?.lat ?? null,
                        longitude: placeData.coordinates?.lng ?? null,
                        place_id: placeData.placeId || null,
                        geo_source: placeData.source || null,
                      }));
                      if (errors.location) {
                        setErrors((prev) => ({ ...prev, location: '' }));
                      }
                    }}
                    required
                    fullWidth
                    error={!!errors.location}
                    helperText={errors.location}
                    sx={locationSx}
                  />
                </Field>
                
                <Field label={t('itinerary.googleMapsLink')}>
                  <TextField
                    name="google_map_link"
                    placeholder={t('itinerary.googleMapsPlaceholder')}
                    value={formData.google_map_link}
                    onChange={(e) => handleFieldChange('google_map_link', e.target.value)}
                    fullWidth
                    variant="standard"
                    sx={underlineSx}
                  />
                </Field>
                
                <Field label={t('itinerary.categoryLabel')}>
                  <TextField
                    select
                    name="category"
                    value={formData.category}
                    onChange={(e) => handleFieldChange('category', e.target.value)}
                    fullWidth
                    variant="standard"
                    sx={underlineSx}
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value} sx={{ py: 1.1 }}>
                        <CategoryOption option={option} label={t(option.labelKey)} />
                      </MenuItem>
                    ))}
                  </TextField>
                </Field>

                {formData.category === 'transport' && (
                  <Field label={t('itinerary.transportationType')}>
                    <TextField
                      select
                      name="transportation_type"
                      value={formData.transportation_type}
                      onChange={(e) => {
                        const nextType = e.target.value;
                        const nextMode = getActivityScheduleMode({
                          category: formData.category,
                          transportationType: nextType,
                        });
                        const { transportationData } = seedTransportTimesFromSchedule({
                          transportationData: {},
                          startDatetime: formData.start_datetime,
                          endDatetime: formData.end_datetime,
                          requiredFields: nextMode.requiredTransportTimeFields || [],
                        });
                        clearSubmitErrors();
                        setFormData((prev) => ({
                          ...prev,
                          transportation_type: nextType,
                          transportation_data: transportationData,
                        }));
                        if (errors.transportation_type || errors.schedule) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.transportation_type;
                            delete next.schedule;
                            (nextMode.requiredTransportTimeFields || []).forEach((fieldName) => {
                              delete next[fieldName];
                            });
                            return next;
                          });
                        }
                        if (
                          nextType === 'flight'
                          || nextMode.source === 'transport_fields'
                          || nextMode.source === 'transport_start_only'
                        ) {
                          setShowTransportationFields(true);
                        }
                        if (nextType === 'flight') {
                          setFlightLegs([{
                            airline: '',
                            flight_number: '',
                            departure_airport: '',
                            arrival_airport: '',
                            departure_time: '',
                            arrival_time: '',
                          }]);
                        }
                      }}
                      fullWidth
                      variant="standard"
                      sx={underlineSx}
                    >
                      {Object.values(TRANSPORTATION_CATEGORIES).map(category => (
                        <MenuItem key={category.name} disabled>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {category.icon} {category.name}
                          </Typography>
                        </MenuItem>
                      ))}
                      {Object.values(TRANSPORTATION_TYPES).map(type => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.icon} {t(`itinerary.transportTypes.${type.id}`, { defaultValue: type.name })}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Field>
                )}

                {/* Transportation Details - Show when transportation type is selected */}
                {formData.category === 'transport' && formData.transportation_type && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => setShowTransportationFields(!showTransportationFields)}
                      endIcon={showTransportationFields ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{
                        mb: 2,
                        borderRadius: 0,
                        borderColor: DOCUMENT.ruleHard,
                        color: DOCUMENT.ink2,
                        fontSize: 11,
                        letterSpacing: '0.11em',
                        textTransform: 'uppercase',
                        '&:hover': { borderColor: DOCUMENT.accent, color: DOCUMENT.ink, bgcolor: 'transparent' },
                      }}
                    >
                      {t('itinerary.transportationDetails')}
                    </Button>
                    
                    <Collapse in={showTransportationFields}>
                      {formData.transportation_type === 'flight' ? (
                        <Box sx={{ mb: 2 }}>
                          <FlightLegsEditor
                            legs={flightLegs}
                            onChange={setFlightLegs}
                            trip={trip}
                          />
                          <Grid container spacing={2} sx={{ mt: 1 }}>
                            {FLIGHT_BOOKING_FIELDS.map(({ name, labelKey }) => (
                              <Grid item xs={12} sm={6} key={name}>
                                <TextField
                                  name={`transportation_data.${name}`}
                                  fullWidth
                                  variant="standard"
                                  label={t(`itinerary.transportFields.${labelKey}`)}
                                  value={formData.transportation_data[name] || ''}
                                  onChange={(e) => {
                                    const { value } = e.target;
                                    setFormData(prev => ({
                                      ...prev,
                                      transportation_data: {
                                        ...prev.transportation_data,
                                        [name]: value,
                                      },
                                    }));
                                  }}
                                  sx={underlineSx}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      ) : (
                      <Grid container spacing={2}>
                        {(() => {
                          const transportType = getTransportationType(formData.transportation_type);
                          if (!transportType || !transportType.fields) return null;
                          
                          // Combine required and optional fields
                          const allFields = [
                            ...transportType.fields.required.map(field => ({ name: field, required: true })),
                            ...transportType.fields.optional.map(field => ({ name: field, required: false }))
                          ];
                          
                          return allFields.map(field => {
                            const isScheduleTimeField = [
                              'departure_time',
                              'pickup_time',
                              'start_time',
                              'arrival_time',
                              'return_time',
                            ].includes(field.name);
                            const fieldRequired = field.required
                              || (scheduleMode.requiredTransportTimeFields || []).includes(field.name);
                            return (
                            <Grid item xs={12} sm={6} key={field.name}>
                              <TextField
                                name={`transportation_data.${field.name}`}
                                fullWidth
                                variant="standard"
                                label={field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                value={formData.transportation_data[field.name] || ''}
                                onChange={(e) => {
                                  const newTransportationData = {
                                    ...formData.transportation_data,
                                    [field.name]: e.target.value
                                  };
                                  
                                  // Auto-populate schedule based on transportation times
                                  setFormData(prev => {
                                    const updates = {
                                      ...prev,
                                      transportation_data: newTransportationData
                                    };
                                    
                                    // Map departure/start times to start_datetime
                                    if (['departure_time', 'pickup_time', 'start_time'].includes(field.name) && e.target.value) {
                                      updates.start_datetime = e.target.value;
                                    }
                                    
                                    // Map arrival/return times to end_datetime
                                    if (['arrival_time', 'return_time'].includes(field.name) && e.target.value) {
                                      updates.end_datetime = e.target.value;
                                    }
                                    
                                    return updates;
                                  });
                                  if (errors[field.name]) {
                                    setErrors((prev) => ({ ...prev, [field.name]: '' }));
                                  }
                                }}
                                type={field.name.includes('time') ? 'datetime-local' : 'text'}
                                required={fieldRequired}
                                error={Boolean(errors[field.name])}
                                helperText={errors[field.name] || undefined}
                                InputLabelProps={{
                                  shrink: field.name.includes('time') ? true : undefined,
                                }}
                                sx={underlineSx}
                              />
                              {isScheduleTimeField && scheduleMode.hideSchedule && field.name === (scheduleMode.requiredTransportTimeFields || [])[0] && scheduleMode.hintKey && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: DOCUMENT.ink3 }}>
                                  {t(scheduleMode.hintKey)}
                                </Typography>
                              )}
                            </Grid>
                            );
                          });
                        })()}
                      </Grid>
                      )}
                    </Collapse>
                  </Box>
                )}
            </Section>

            {showVisibility && (showVisibilitySelect
              || (formData.visibility === 'personal' && canAssignPersonalOwner && personalOwnerOptions.length > 0)
              || formData.visibility === 'subgroup') && (
            <Section title={t('activityModal.visibilitySection', { defaultValue: 'Visibility & Participants' })}>
              {showVisibilitySelect ? (
                <Field label={t('activityModal.whoCanSee', { defaultValue: 'Who can see this activity?' })}>
                  <TextField
                    select
                    value={formData.visibility}
                    onChange={(e) => {
                      handleFieldChange('visibility', e.target.value);
                    }}
                    fullWidth
                    variant="standard"
                    sx={underlineSx}
                  >
                    <MenuItem value="personal">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" />
                        {canAssignPersonalOwner
                          ? t('activityModal.personalForMember', { defaultValue: 'Personal (one traveler)' })
                          : t('activityModal.personalOnlyYou', { defaultValue: 'Personal (Only you)' })}
                      </Box>
                    </MenuItem>
                    <MenuItem value="subgroup">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupIcon fontSize="small" />
                        {t('activityModal.subgroupSelected', { defaultValue: 'Sub-Group (Selected members)' })}
                      </Box>
                    </MenuItem>
                  </TextField>
                </Field>
              ) : null}

              {formData.visibility === 'personal' && canAssignPersonalOwner && personalOwnerOptions.length > 0 && (
                <Box sx={{ mt: showVisibilitySelect ? 2 : 0 }}>
                  <Field label={t('activityModal.personalOwnerLabel', { defaultValue: 'Whose personal itinerary?' })}>
                    <TextField
                      select
                      fullWidth
                      variant="standard"
                      data-testid="personal-owner-select"
                      value={formData.owner_user_id || ''}
                      onChange={(e) => handleFieldChange('owner_user_id', Number(e.target.value))}
                      sx={underlineSx}
                    >
                      {personalOwnerOptions.map((member) => (
                        <MenuItem key={member.user_id} value={member.user_id}>
                          {member.username || member.email}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Field>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: DOCUMENT.ink3 }}>
                    {t('activityModal.personalOwnerHint', {
                      defaultValue: 'Use this for flights or hotels when someone is traveling from a different city.',
                    })}
                  </Typography>
                  {errors.owner_user_id && (
                    <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                      {errors.owner_user_id}
                    </Typography>
                  )}
                </Box>
              )}
              
              {formData.visibility === 'subgroup' && (
                <Box sx={{ mt: showVisibilitySelect ? 2 : 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={documentFormLabelSx}>
                      Select Participants
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => {
                          handleFieldChange('participants', tripMembers.map(getParticipantKey));
                        }}
                        sx={ghostSx}
                      >
                        Select All
                      </Box>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => {
                          handleFieldChange('participants', []);
                        }}
                        sx={ghostSx}
                      >
                        Clear All
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ maxHeight: 200, overflow: 'auto', border: `1px solid ${DOCUMENT.ruleHard}`, bgcolor: DOCUMENT.surface, p: 1 }}>
                    {tripMembers.map((member) => {
                      const participantKey = getParticipantKey(member);
                      const pendingLabelKey = PARTICIPANT_STATUS_LABEL_KEYS[member.status];
                      return (
                      <Box
                        key={participantKey}
                        component="label"
                        data-testid={`participant-option-${participantKey}`}
                        sx={{ display: 'flex', alignItems: 'center', p: 1, borderBottom: `1px solid ${DOCUMENT.rule}`, cursor: 'pointer' }}
                      >
                        <Checkbox
                          checked={selectedParticipantKeys.has(participantKey)}
                          onChange={(e) => {
                            const newParticipants = e.target.checked
                              ? [...formData.participants, participantKey]
                              : formData.participants.filter((p) => normalizeParticipantKey(p) !== participantKey);
                            handleFieldChange('participants', newParticipants);
                          }}
                          size="small"
                          sx={{ color: DOCUMENT.ink3, '&.Mui-checked': { color: DOCUMENT.oxblood } }}
                        />
                        <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: DOCUMENT.oxblood, color: DOCUMENT.ivory, borderRadius: 0 }}>
                          {member.username?.charAt(0)?.toUpperCase() || '?'}
                        </Avatar>
                        <Typography sx={{ fontSize: 14, color: DOCUMENT.ink, flex: 1, minWidth: 0 }} noWrap>
                          {member.username}
                        </Typography>
                        {pendingLabelKey && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t(pendingLabelKey)}
                            sx={{ ml: 1, borderColor: DOCUMENT.ruleHard, color: DOCUMENT.ink3 }}
                          />
                        )}
                      </Box>
                      );
                    })}
                  </Box>
                  {tripMembers.some((member) => PARTICIPANT_STATUS_LABEL_KEYS[member.status]) && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: DOCUMENT.ink3 }}>
                      {t('activityModal.invitedParticipantsHint')}
                    </Typography>
                  )}
                  {errors.participants && (
                    <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                      {errors.participants}
                    </Typography>
                  )}
                </Box>
              )}
            </Section>
            )}

            {!scheduleMode.hideSchedule && (
            <Section title={t(scheduleMode.scheduleTitleKey || 'itinerary.scheduleLabel')}>
              {scheduleMode.hintKey && (
                <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: DOCUMENT.ink3 }}>
                  {t(scheduleMode.hintKey)}
                </Typography>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: scheduleMode.hideStart ? '1fr' : '1fr 1fr' }, columnGap: 3.25 }}>
                {!scheduleMode.hideStart && (
                <Field label={t(scheduleMode.startLabelKey || 'itinerary.startDateTime')} required>
                  <TextField
                    name="start_datetime"
                    type="datetime-local"
                    value={formData.start_datetime}
                    onChange={(e) => handleFieldChange('start_datetime', e.target.value)}
                    error={!!errors.start_datetime}
                    helperText={errors.start_datetime}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: tripDateTimeBounds(trip)?.min,
                      max: tripDateTimeBounds(trip)?.max,
                      'aria-label': t(scheduleMode.startLabelKey || 'itinerary.startDateTime'),
                    }}
                    fullWidth
                    variant="standard"
                    sx={underlineSx}
                  />
                </Field>
                )}
                <Field label={t(scheduleMode.endLabelKey || 'itinerary.endDateTime')} required>
                  <TextField
                    name="end_datetime"
                    type="datetime-local"
                    value={formData.end_datetime}
                    onChange={(e) => handleFieldChange('end_datetime', e.target.value)}
                    error={!!errors.end_datetime}
                    helperText={errors.end_datetime}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: formData.start_datetime || tripDateTimeBounds(trip)?.min,
                      max: tripDateTimeBounds(trip)?.max,
                      'aria-label': t(scheduleMode.endLabelKey || 'itinerary.endDateTime'),
                    }}
                    fullWidth
                    variant="standard"
                    sx={underlineSx}
                  />
                </Field>
              </Box>
            </Section>
            )}
            {scheduleMode.hideSchedule && scheduleMode.source === 'flight_legs' && (
              <Box sx={{ mb: 2 }}>
                {scheduleMode.hintKey && (
                  <Typography variant="caption" sx={{ display: 'block', color: DOCUMENT.ink3 }}>
                    {t(scheduleMode.hintKey)}
                  </Typography>
                )}
                {errors.schedule && (
                  <Typography color="error" variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    {errors.schedule}
                  </Typography>
                )}
              </Box>
            )}
            
            <Section title={t('itinerary.detailsLabel')}>
              <Field label={t('itinerary.itemPhoto')} hint={t('itinerary.itemPhotoHint')}>
                <SingleImageUploader
                  value={formData.image_url || ''}
                  onChange={(url) => handleFieldChange('image_url', url || '')}
                  preset="itinerary"
                  folder="itinerary"
                  label={t('itinerary.addItemPhoto')}
                  height={160}
                  cropShape="rect"
                />
              </Field>
              <Field label={t('itinerary.descriptionLabel')}>
                <TextField
                  placeholder={t('itinerary.descriptionPlaceholder')}
                  value={formData.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                  variant="standard"
                  sx={underlineSx}
                />
              </Field>
              
              {showPackageAddOn && (
                <Field label={t('activityModal.packageRole')} hint={formData.is_included === false ? t('activityModal.packageAddOnHint') : t('activityModal.packageIncludedHint')}>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {[
                      { value: true, label: t('activityModal.packageIncluded') },
                      { value: false, label: t('activityModal.packageAddOn') },
                    ].map((option) => {
                      const selected = (formData.is_included !== false) === option.value;
                      return (
                        <Box
                          key={String(option.value)}
                          component="button"
                          type="button"
                          data-testid={option.value ? 'package-item-included' : 'package-item-addon'}
                          onClick={() => handleFieldChange('is_included', option.value)}
                          sx={{
                            ...ghostSx,
                            border: `1px solid ${selected ? DOCUMENT.accent : DOCUMENT.ruleHard}`,
                            color: selected ? DOCUMENT.ink : DOCUMENT.ink2,
                            px: 1.5,
                            py: 0.85,
                          }}
                        >
                          {option.label}
                        </Box>
                      );
                    })}
                  </Box>
                </Field>
              )}

              {(showCost || (showPackageAddOn && formData.is_included === false)) && (
                <Field
                  label={showPackageAddOn && formData.is_included === false ? t('activityModal.addOnPrice') : t('itinerary.costOptional')}
                  hint={showPackageAddOn && formData.is_included === false ? t('activityModal.packageAddOnHint') : 'Add the cost or estimated cost for this activity'}
                  required={showPackageAddOn && formData.is_included === false}
                >
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 2.25 }}>
                    <NumericTextField
                      placeholder={t('itinerary.enterAmount')}
                      value={formData.cost}
                      onValueChange={(value) => handleFieldChange('cost', value)}
                      allowDecimal
                      allowEmpty
                      fullWidth
                      variant="standard"
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1, color: DOCUMENT.ink3 }}>
                          {getCurrencySymbol(formData.cost_currency)}
                        </Typography>,
                      }}
                      sx={underlineSx}
                    />
                    <TextField
                      select
                      name="cost_currency"
                      value={formData.cost_currency}
                      onChange={(e) => handleFieldChange('cost_currency', e.target.value)}
                      variant="standard"
                      sx={underlineSx}
                    >
                      {CURRENCIES.map((currency) => (
                        <MenuItem key={currency.code} value={currency.code}>
                          {currency.symbol} {currency.code}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Field>
              )}
            </Section>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          px: { xs: 2.25, sm: 3.5 },
          py: 2,
          bgcolor: DOCUMENT.canvas,
          gap: 2.25,
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
          alignItems: { xs: 'stretch', sm: 'center' },
          borderTop: `1px solid ${DOCUMENT.ruleHard}`,
          flexShrink: 0,
        }}>
          <Button
            onClick={onClose}
            sx={{
              ...documentOutlineButtonSx,
              minWidth: 120,
              alignSelf: { xs: 'stretch', sm: 'center' },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={submitting}
            sx={primaryBtnSx}
          >
            {submitting ? t('itinerary.saving') : (mode === 'create' ? t('itinerary.createActivity') : t('itinerary.updateActivity'))}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ActivityModal;
